import React, {
    createContext,
    useState,
    useEffect,
    ReactNode,
    useContext,
    useCallback,
    useRef,
    useMemo
} from 'react';

import axios from 'axios';
import BN from 'bn.js';
import moment from 'moment';
import i18next from 'i18next';
import crypto from 'crypto';

import { mnemonicNew, mnemonicToWalletKey } from '@ton/crypto';
import { 
    TonClient, 
    Address,
    WalletContractV4, 
    WalletContractV5R1, 
    JettonMaster, 
    JettonWallet, 
    internal,
    external,
    SendMode,
    storeMessage } from '@ton/ton';
import { 
    beginCell, 
    Cell, 
    Message, 
    toNano, 
    fromNano 
} from '@ton/core';
// import { mnemonicNew, mnemonicToWalletKey } from 'ton-crypto';
// import { 
//     TonClient, 
//     Address, 
//     WalletContractV4, 
//     fromNano, 
//     JettonMaster, 
//     JettonWallet, 
//     internal, 
//     SendMode } from 'ton';
// import { beginCell, Message } from 'ton-core';

import { sleep } from '../utils/utils';
import { tgShowAlert } from '../utils/telegramPopup';
import { getTelegramId } from '../utils/telegramClientData';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'react-interval-hook';
import { DefaultTokenContext } from './DefaultTokenContext';
import useSecureStorage from '../hooks/useSecureStorage';

// WASM 모듈 import
import { initializeWasm } from '../wasm/wasmutils';
import { 
    fetch_transaction_data, 
    add_server_tx_history, 
    get_server_tx_id_list,
    get_server_tx_history 
} from '../wasm/rust_wassm_fetch';

// types
import { SYNC_STATUS, SyncTx } from './types/NetworkTypes';

interface TonContextProps {
    networkName: string;
    balance: number;
    countSyncProcess: number;
    countSyncComplete: number;
    syncStatus: string;
    lastSyncTxs: SyncTx[];
    reqTxStop: boolean;

    startDbSyncTimer: () => void;
    stopDbSyncTimer: () => void;

    getBalance: (walletAddress: string, isFormatting?: boolean, updateType?: string) => Promise<number>;
    getBalanceFromServer: (walletAddress: string, coinName: string) => Promise<number>;
    getTokenBalance: (walletAddress: string,  mintAddress: string, isFormatting?: boolean, updateType?: string) => Promise<string>;
    getTokenBalanceFromServer: (
        walletAddress: string,
        mintAddress: string,
        coinName: string
    ) => Promise<number>;
    estimateGas: (walletAddress: string) => Promise<string>;
    sendTransactionWithPrivateKey: (
        privateKey: string,
        toAddress: string,
        amount: string,
        tokenSymbol: string,
        tokenAddress: string
    ) => Promise<string>;
    getTotalTxCount: (walletAddress: string) => Promise<any>;
    stopRequestTxData: (isStop?: boolean) => void;
    syncAllTransaction: (walletAddress: string) => Promise<any>;
    getTokenInfo: (mintAddress: string) => Promise<any>;
    getServerTxHistory: (walletAddress: string, network: string, tid: string, tokenName: string, page: number, take: number) => Promise<any>;
    getBlockScanUrl: (txid: string) => Promise<string>;
    isValidAddress: (address: string) => Promise<Boolean>;
}


const defaultContext: TonContextProps = {
    networkName: '',
    balance: 0,
    countSyncProcess: 0,
    countSyncComplete: 0,
    syncStatus: SYNC_STATUS.CHECKING,
    lastSyncTxs: [],
    reqTxStop: false,

    startDbSyncTimer: () => {},
    stopDbSyncTimer: () => {},
    getBalance: async (walletAddress: string, isFormatting = true, updateType = 'await') => Promise.resolve(0),
    getBalanceFromServer: async () => Promise.resolve(0),
    getTokenBalance: async (walletAddress: string, mintAddress: string, isFormatting = true, updateType = 'await') => Promise.resolve('0.0'),
    getTokenBalanceFromServer: async () => Promise.resolve(0),
    estimateGas: async () => Promise.resolve('0'),
    sendTransactionWithPrivateKey: async () => '',
    getTotalTxCount: async () => Promise.resolve(0),
    stopRequestTxData: () => {},
    syncAllTransaction: async () => Promise.resolve([]) ,
    getTokenInfo: async () => Promise.resolve(null),
    getServerTxHistory: async () => Promise.resolve([]),
    getBlockScanUrl: async (txid: string) => Promise.resolve(''),
    isValidAddress: async (address: string) => Promise.resolve(false)
};

  export const TonContext = createContext<TonContextProps>(defaultContext);

  // 일부 캐시 상수
  const cacheExpiryTime = 10000;
  const gasPriceCacheExpiryTime = 10000;
  const reqDelay = 800; // 요청간 지연
  const dbTxInterval = 60000; // useInterval 주기
  const reqdbTxSyncInterval = 20000; // 최소 20초 간격으로만 syncAllTransaction 가능

  const BLOCK_RANGE = 9999; // BSC 제한이 10,000이므로 안전하게 9,999 사용

  export const TonProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();

    const [networkName, setNetworkName] = useState('ton');
    const [balance, setBalance] = useState(0);
    const [countSyncProcess, setCountSyncProcess] = useState(0);
    const [countSyncComplete, setCountSyncComplete] = useState(0);
    const [syncStatus, setSyncStatus] = useState(SYNC_STATUS.COMPLETE);
    const [lastSyncTxs, setLastSyncTxs] = useState<SyncTx[]>([]);
    const [wasmInitialized, setWasmInitialized] = useState(false);

    const [tokenBalanceCache, setTokenBalanceCache] = useState<any>({});
    const [gasPriceCache, setGasPriceCache] = useState<any>({});
    const [reqDbTxInterval, setReqDbTxInterval] = useState(0);

    // 참조
    const [reqTxStop, setReqTxStop] = useState(false);
    const reqTxStopRef = useRef(reqTxStop);
    const [syncKey, setSyncKey] = useState('');
    const syncKeyRef = useRef(syncKey);
    const reqDbTxIntervalRef = useRef(reqDbTxInterval);

    const [isRunAllTransactionHistory, setIsRunAllTransactionHistory] = useState(false);
    const runAllTransactionHistoryRef = useRef(isRunAllTransactionHistory);

    const [isRunLimitTransactionHistory, setIsRunLimitTransactionHistory] = useState(false);
    const runLimitTransactionHistoryRef = useRef(isRunLimitTransactionHistory);

    const { getBlockChain, getWalletAddress } = useSecureStorage();

    const defaultTokenContext = useContext(DefaultTokenContext);
	const tonNetwork = defaultTokenContext.searchNetwork('ton');

    // TON client
    const client = useMemo(() => {
        console.log(`defaultTokenContext searchNetwork Init : ton `, tonNetwork)

		// DefaultTokenContext에서 'solana' 네트워크 정보를 가져옴
		if (!tonNetwork) {
			console.warn(`TonContext client tonNetwork is null`)
            return null;
        }

		// 네트워크 정보가 없으면 fallback으로 env 값을 사용
		const rpcUrl = tonNetwork?.rpc_http || '';

		return new TonClient({
            endpoint: rpcUrl
        });
	}, [tonNetwork]);

    // ─────────────────────────────────────────────────────────────
    // useEffect: 초기화 등 (원래 Solana WASM init 등)
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (wasmInitialized) return; // 이미 초기화된 경우 재실행 막기

        const initNetwork = async () => {
            try {
            console.warn(`TonContext 웹 어셈블리 init`)
            // WASM init이 필요없다면 제거
            // await init();
            await initializeWasm();
            
            setWasmInitialized(true);
            } catch (error) {
            console.error(`TonContext 웹 어셈블리 init error : `, error)
            }
        };

        initNetwork();
    }, [wasmInitialized]);

    // 각 ref 동기화
    useEffect(() => {
        reqTxStopRef.current = reqTxStop;
    }, [reqTxStop]);

    useEffect(() => {
        runAllTransactionHistoryRef.current = isRunAllTransactionHistory;
    }, [isRunAllTransactionHistory]);

    useEffect(() => {
        runLimitTransactionHistoryRef.current = isRunLimitTransactionHistory;
    }, [isRunLimitTransactionHistory]);

    useEffect(() => {
        syncKeyRef.current = syncKey;
    }, [syncKey]);

    useEffect(() => {
        reqDbTxIntervalRef.current = reqDbTxInterval;
    }, [reqDbTxInterval]);

    // ─────────────────────────────────────────────────────────────
    // Timer 예시
    // ─────────────────────────────────────────────────────────────
    const syncDbTxTimer = async () => {
        const walletAddress = await getWalletAddress();
        syncAllTransaction(walletAddress)
        .then(() => {
            stop();
        })
        .catch((error) => {
            console.error(`syncDbTxTimer error:`, error);
            stop();
        });
    };

    const { start, stop, isActive } = useInterval(syncDbTxTimer, dbTxInterval, {
        autoStart: false,
        immediate: true,
        selfCorrecting: true,
        onFinish: () => {
            console.log('useInterval Callback when timer is stopped');
            stop();
        }
    });

    /** DB TX 현행화 타이머 시작 */
    const startDbSyncTimer = async () => {
        const now = Date.now();
        const lastRequestTime = reqDbTxIntervalRef.current;
        if (now - lastRequestTime < reqdbTxSyncInterval) {
            console.warn(`DB TX 현행화: 아직 20초 안 지남`);
            await tgShowAlert(t('balance_screen_message_14'));
            return;
        }
        setReqDbTxInterval(now);

        console.log(`defaultTokenContext startDbSyncTimer client :  `, client)

        const walletAddress = await getWalletAddress();
        await syncAllTransaction(walletAddress);
    };

    /** DB TX 현행화 타이머 종료 */
    const stopDbSyncTimer = async () => {
        console.log(`DB TX 현행화 스케줄러 종료`);
        stop();
    };
    

    /**
     * TON 단위 포맷팅
     * @param value 
     * @param decimals 
     * @returns 
     */
    const formatTonUnits = (
        value: bigint, 
        decimals: number
    ): string => {
        const scale = 10n ** BigInt(decimals);
        const intPart = value / scale;
        const fracPart = value % scale;
        let fracStr = fracPart.toString();
        while (fracStr.length < decimals) {
            fracStr = "0" + fracStr;
        }
        // 불필요한 후행 0 제거 (없으면 정수만 반환)
        fracStr = fracStr.replace(/0+$/, "");
        return fracStr ? `${intPart.toString()}.${fracStr}` : intPart.toString();
    };

    /**
     * 지갑 주소를 hex 형식으로 변환
     * @param address 
     * @returns 
     */
    const convertWalletAddressToHex = (
        address: string
    ): string => {
        const addr = Address.parse(address);
        const wc = addr.workChain;
        const hexHash = addr.hash.toString("hex").toUpperCase(); // 대문자 변환은 편의
        const hexWalletAddress = `${wc}:${hexHash}`;
        return hexWalletAddress;
    }

    /**
     * 지갑 주소를 hex 형식으로 변환
     * @param address 
     * @returns 
     */
    const convertHexToWalletAddress = (
        hexAddress: string, 
        isBounceable: boolean = false
    ): string => {
        if(!hexAddress) return '';

        const addr = Address.parse(hexAddress);
        return addr.toString({ bounceable: isBounceable });
    }

    /**
     * 트랜잭션 hash base 64를 hex 형식으로 변환
     */
    const convertHashTpHex = (
        hash: string
    ): string => {
        if(!hash) return '';

        const hexStr = Buffer.from(hash, 'base64').toString('hex');
        return hexStr;
    }

    /**
     * 지정한 주소의 토큰 데이터를 JSON‑RPC 요청으로 가져와서 반환
     * @param address 토큰 마스터 컨트랙트 주소 (예: USDT jetton master 주소)
     * @returns JSON 배열 (예: [ { ok: true, result: { ... }, jsonrpc: "2.0", id: "1" } ])
     */
    const getTokenDataFromRpc = async (
        address: string
    ): Promise<any> => {
        const payload = {
            jsonrpc: "2.0",
            id: 1,
            method: "getTokenData",
            params: {
                address: address
            }
        };
    
        try {
            const rpcHttpUrl = tonNetwork?.rpc_http || ''

            const response = await axios.post(
                rpcHttpUrl as string,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );
            console.log(`getTokenDataFromRpc response: `, response);
            
            // ok가 true인 경우에만 result 데이터를 배열로 반환
            if (response.data.ok === true) {
                // console.log(`getTokenDataFromRpc response.data.result: `, response.data.result);
                const jsonData = response.data.result
                console.log(`getTokenDataFromRpc jsonData: `, jsonData);
        
                return jsonData;
            }
        } catch (error) {
        console.error("Error fetching token data:", error);
        throw error;
        }
        return [];
    };


    /**
     * Quicknode TON API에서 토큰의 decimal 값을 조회하는 함수
     * @param mintAddress 
     * @returns 
     */
    const getTokenDecimalsFromQuicknode = async (
        mintAddress: string
    ): Promise<number> => {
        const payload = {
            jsonrpc: "2.0",
            id: 1,
            method: "getTokenData",
            params: {
                address: mintAddress
            }
        };
        
        try {
            const rpcHttpUrl = tonNetwork?.rpc_http || ''

            const response = await axios.post(
                rpcHttpUrl as string,
                payload,
                    { headers: { "Content-Type": "application/json" } }
            );
            
            // 예: response.data.result.decimals 가 토큰의 decimal 값을 포함함.

            const decimals = response.data?.result?.decimals;
            return decimals ? Number(decimals) : 9;
        } catch (error) {
            console.error("Error fetching token decimals:", error);
            return 9;
        }
    };


    /**
     * 지정한 지갑 주소의 마지막 tx hash 정보를 가져온다
     * @param address 지갑 주소
     * @returns tx hash 정보
     */
    const getWalletLastTxHashFromRpc = async (
        walletAddress: string
    ): Promise<any> => {
        const payload = {
            jsonrpc: "2.0",
            id: 1,
            method: "getWalletInformation",
            params: {
                address: walletAddress
            }
        };
    
        try {
            const rpcHttpUrl = tonNetwork?.rpc_http || ''
            const response = await axios.post(
                rpcHttpUrl as string,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );
            console.log(`getWalletLastTxHashFromRpc response: `, response);
            
            // ok가 true인 경우에만 result 데이터를 배열로 반환
            if (response.data.ok === true) {
                // console.log(`getTokenDataFromRpc response.data.result: `, response.data.result);
                const jsonData = response.data.result
                console.log(`getWalletLastTxHashFromRpc jsonData: `, jsonData);
                
                const result = {
                    seqno: jsonData.seqno,
                    txHash: jsonData.last_transaction_id.hash,
                }
                return result;
            }
        } catch (error) {
            console.error("Error wallet last tx data:", error);
            throw error;
        }

        return null;
    };


    /**
     * 지정한 주소의 트랜잭션 데이터를 JSON‑RPC 요청을 통해 가져오고 전체 응답 객체 반환.
     *
     * @param address 
     * @param startLt : 트랜잭션 시작 lt 번호
     * @param hash : 트랜잭션 시작 hash
     * @param limit : 한번에 가져올 트랜잭션 개수
     * @returns 
     */
    // const getTrasactionsFromRpc = async (
    //     address: string, 
    //     startLt:number=0, 
    //     hash:string='', 
    //     limit:number=100
    // ): Promise<any> => {
        
    //     // 범위 검색 조건 있는 경우
    //     let payload
    //     if(0 < startLt && '' !== hash) {
    //         payload = {
    //             method: "getTransactions",
    //             params: { 
    //                 address: address,
    //                 lt: startLt,
    //                 hash: hash,
    //                 limit: limit
    //             },
    //             id: "1",
    //             jsonrpc: "2.0",
    //         };
    //     } else {
    //         payload = {
    //             method: "getTransactions",
    //             params: { 
    //                 address: address 
    //             },
    //             id: "1",
    //             jsonrpc: "2.0",
    //         };
    //     }

    //     try {
    //         const response = await axios.post(
    //             process.env.REACT_APP_TON_RPC_URL as string,
    //             payload,
    //             { headers: { "Content-Type": "application/json" } }
    //         );
    //         console.log(`getWalletLastTxHashFromRpc response: `, response);        
    //         // ok가 true인 경우에만 result 데이터를 배열로 반환
    //         if (response.data.ok === true) {
    //             const jsonData = response.data.result
    //             return jsonData;
    //         }
    //     } catch (error) {
    //         console.error("Error fetching transactions data:", error);
    //         throw error;
    //     }
    //         return [];
    // }

    /**
     * 지정한 주소의 트랜잭션 데이터를 indexer api 요청을 통해 가져오고 전체 응답 객체를 반환.
     * @param address 
     * @param offeset 
     * @param limit 
     * @returns 
     */
    const getTrasactionsFromIndexerAPI = async (
        ownerAddress: string,
        offeset: number=0,
        limit: number=100,
    ): Promise<any> => {
        try {
            const scanApiUrl = tonNetwork?.scan || ''
            const apiUrl = `${scanApiUrl}/transactions`;
            const response = await axios.get(apiUrl, {
            params: {
                account: ownerAddress,
                limit: limit,
                offset: offeset,
                sort: "desc"
            }
            });
            console.log(`getTokenTransferDataFromIndexerAPI response: `, response.data);
            // ok가 true인 경우에만 result 데이터를 배열로 반환
            if (response.data) {
            // console.log(`getTokenDataFromRpc response.data.result: `, response.data.result);
            const jsonData = response.data
            // console.log(`getTokenTransferDataFromIndexerAPI jsonData transactions : `, jsonData.transactions);

            return jsonData.transactions;
            }
        } catch (error) {
            console.error("Error fetching token data:", error);
            throw error;
        }
        return [];
        
    }

    /**
     * 지정한 주소의 토큰 트랜잭션 데이터를 indexer api 요청을 통해 가져오고 전체 응답 객체를 반환.
     * @param address 메인 지갑주소
     * @param contractAddress 토큰 컨트랙트 주소 (예: jetton master 주소)
     * @param limit 가져올 row 수
     * @param offeset skip할 row 수
     * @returns JSON 배열 (예: [ { ok: true, result: { ... }, jsonrpc: "2.0", id: "1" } ])
     */
    const getTokenTransferDataFromIndexerAPI = async (
        ownerAddress: string,
        contractAddress: string,
        offeset: number=0,
        limit: number=100,
    ): Promise<any> => {
        try {
            const scanApiUrl = tonNetwork?.scan || ''
            const apiUrl = `${scanApiUrl}/jetton/transfers`;
            const response = await axios.get(apiUrl, {
            params: {
                owner_address: ownerAddress,
                jetton_master: contractAddress,
                limit: limit,
                offset: offeset,
                sort: "desc"
            }
            });
            // console.log(`getTokenTransferDataFromIndexerAPI response: `, response.data);
            console.log(`getTokenTransferDataFromIndexerAPI response jetton_transfers: `, response.data.jetton_transfers);
            // ok가 true인 경우에만 result 데이터를 배열로 반환
            if (response.data) {
            // console.log(`getTokenDataFromRpc response.data.result: `, response.data.result);
            const jsonData = response.data
            console.log(`getTokenTransferDataFromIndexerAPI jsonData: `, jsonData);
            console.log(`getTokenTransferDataFromIndexerAPI jsonData jetton_transfers : `, jsonData.jetton_transfers);

            return jsonData.jetton_transfers;
            }
        } catch (error) {
            console.error("Error fetching token data:", error);
            throw error;
        }
        return [];
    };


    /**
     * RPC 에서 BNB 잔액 조회
     * @param walletAddress 
     * @param isFormatting 
     * @param updateType : 업데이트 요청 await, promise 방식 선택 
     * @returns 
     */
    const getBalance = async (
        walletAddress: string, 
        isFormatting=true, 
        updateType='await'
    ): Promise<number> => {
        try {
            const cacheKey = 'coinbalance_'+tonNetwork?.symbol.toLowerCase();

            let cachedData = 0;

            if('await' === updateType) {
                const temp = await getBalanceFromRpc(walletAddress)
                localStorage.setItem(cacheKey, temp.toString());
                cachedData = temp;
            } else if('promise' === updateType) {
                getBalanceFromRpc(walletAddress)
                .then((response) => {
                    // 코인 수량 원본 저장
                    const temp = response.toString() ? response.toString() : '0';
                    localStorage.setItem(cacheKey, temp.toString());
                })
                .catch((error) => {})
                
                const temp = localStorage.getItem(cacheKey);
                cachedData = Number(temp?.toString() ?? '0')
            } else if('cache' === updateType) {
                const temp = localStorage.getItem(cacheKey);
                cachedData = Number(temp?.toString() ?? '0');
            } else {
                const temp = localStorage.getItem(cacheKey);
                cachedData = Number(temp?.toString() ?? '0');
            }

            // 3) 형식화
            //    - isFormatting = true인 경우 TON 단위로 변환
            //    - false면 nanoTON 수치로 반환
            if (!isFormatting) {
                const balanceNano = Number(cachedData?.toString() ?? '0');
                console.log(`TON 코인 보유수량(nano) : ${balanceNano}`)
                return balanceNano;
            }

            // 소수점 변경
            const balanceTon = fromNano(Number(cachedData?.toString() ?? '0'));
            const formattedBalance = Number(balanceTon.toString());

            console.log(`TON 코인 보유수량 : ${formattedBalance}`)

            return formattedBalance;
        } catch (error) {
            console.error(`getBalanceFromCache error: `, error);
            alert(t('rpc_req_message_01'))
            return 0;
        }
    };

    const getBalanceFromRpc = async (
        walletAddress: string
    ): Promise<number> => {
        try {
            if (!client) {
				console.error('client is not initialized');
				return 0;
			}

             // 1) 문자열 주소를 Address 객체로 파싱
            const address = Address.parse(walletAddress);
            console.log(`TonContext getBalance address: ${address}`);

            // 2) TonClient의 getBalance로 나노톤(nanoTON) 단위의 BN(BigNumber) 반환
            const balanceBN = await client.getBalance(address);
            console.log(`TonContext getBalance balanceBN: ${balanceBN}`);

            return Number(balanceBN.toString());
        } catch (error) {
            console.error(`getBalance error: `, error);
            alert(t('rpc_req_message_01'))
            return 0;
        }
    };

    /**
     * 서버에서 BNB 잔액 가져오기
     * @param walletAddress 
     * @param coinName 
     * @param isFormatting 
     * @returns 
     */
    const getBalanceFromServer = async (
        walletAddress: string, 
        coinName: string, 
        isFormatting = true
    ): Promise<number> => {
        if (!walletAddress || !coinName) return 0;
    
        const network = await getBlockChain();
        const telegramId = await getTelegramId();
        if (!network || !telegramId) {
            console.warn(`getBalanceFromServer: no network or telegramId`);
            return 0;
        }
    
        try {
            const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balance`, {
                wallet_address: walletAddress,
                network,
                coin_name: coinName,
                tid: telegramId
            });
    
            if (response.data?.result === true) {
                const rawValue = response.data.data?.ta_total_amount ?? '0';
            
                if (!isFormatting) {
                    return Number(rawValue.toString());
                }

                // 소수점 변경
                const balanceTon = fromNano(Number(rawValue.toString()));
                const balanceNano = Number(balanceTon.toString());
                return balanceNano
            }
            return 0;
        } catch (err) {
            console.error('getBalanceFromServer error:', err);
            alert('An issue occurred while fetching the coin balance from the server')
            return 0;
        }
    };


    /**
     * TON 네트워크에서 지정한 지갑주소의 지정한 제트턴(Jetton) 보유 잔액을 조회하는 함수
     * @param walletAddress - 사용자 지갑 주소 (owner 주소)
     * @param contractAddress - 제트턴 마스터 컨트랙트 주소 (토큰의 기본 컨트랙트 주소)
     * @param isFormatting - 포맷 적용 여부 (true면 decimals에 맞게 소수점으로 변환)
     * @param isUpdate - rpc 잔고 조회 후 캐시에 저장 여부
     * @returns 잔액을 문자열로 반환 (포맷된 값 또는 원시 BigInt 문자열)
     */
    const getTokenBalance = async (
        walletAddress: string,
        contractAddressStr: string,
        isFormatting = true,
        updateType='await'
    ): Promise<string> => {
        try {
            const cacheKey = 'tokenbalance_'+tonNetwork?.symbol.toLowerCase()+'_'+contractAddressStr;
            
            let cachedData;

            if('await' === updateType) {
                const temp = await getTokenBalanceFromRpc(walletAddress, contractAddressStr);
                const balance = temp.tokenBalance.toString() ? temp.tokenBalance.toString() : '0';
                const decimals = temp.decimals.toString() ? temp.decimals.toString() : '0';
                cachedData = {tokenBalance:balance, decimals:decimals};
                localStorage.setItem(cacheKey, JSON.stringify(cachedData));

            } else if('promise' === updateType) {
                getTokenBalanceFromRpc(walletAddress, contractAddressStr)
                .then((response) => {
                    // 토큰 수량 원본 저장
                    const balance = response.tokenBalance.toString() ? response.tokenBalance.toString() : '0';
                    const decimals = response.decimals.toString() ? response.decimals.toString() : '0';
                    localStorage.setItem(cacheKey, JSON.stringify({tokenBalance:balance, decimals:decimals}));
                })
                .catch((error) => {})
                
                const temp = localStorage.getItem(cacheKey);
                cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
            } else if('cache' === updateType) {
                const temp = localStorage.getItem(cacheKey);
                cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
            } else {
                const temp = localStorage.getItem(cacheKey);
                cachedData = JSON.parse(temp ?? '{"tokenBalance":0,"decimals":0}');
            }
            
            let finalVal = '0'
            if(cachedData) {
                finalVal = !isFormatting ? cachedData.tokenBalance.toString() : formatTonUnits(BigInt(cachedData.tokenBalance), cachedData.decimals);
            }
            
            console.log(`TON jetton 토큰 보유수량 : ${finalVal}`)

            return finalVal.toString();
        } catch (error) {
            console.error("getTokenBalance error:", error);
            return "0";
        }
    };

    /**
     * TON 네트워크에서 지정한 지갑주소의 지정한 제트턴(Jetton) 보유 잔액을 조회하는 함수
     * @param walletAddress - 사용자 지갑 주소 (owner 주소)
     * @param contractAddress - 제트턴 마스터 컨트랙트 주소 (토큰의 기본 컨트랙트 주소)
     * @param isFormatting - 포맷 적용 여부 (true면 decimals에 맞게 소수점으로 변환)
     * @returns {tokenBalance:number, decimals:number}
     */
    const getTokenBalanceFromRpc = async (
        walletAddress: string,
        contractAddressStr: string,
    ): Promise<{tokenBalance:number, decimals:number}> => {
        try {
            if (!client) {
				console.error('client is not initialized');
				return {tokenBalance:0, decimals:0};
			}

            // 주소 파싱
            const ownerAddr = Address.parse(walletAddress.trim());
            const masterAddr = Address.parse(contractAddressStr.trim()); // jetton 토큰 주소로 masterAddr 생성
            console.log(`getTokenBalance ownerAddr non bounceable : `, ownerAddr.toString({ bounceable: false }));
            console.log(`getTokenBalance ownerAddr bounceable : `, ownerAddr.toString({ bounceable: true }));
            console.log(`getTokenBalance masterAddr: `, masterAddr.toString());

            // 블록익스플로러에서 확인되는 USDT 제트턴 지갑주소
            // EQAPofcUxCdR3VkG1yfeDgbnUB1NVE9AfryQZsbLv3M69iXv
            // JettonMaster 인스턴스 생성 및 getWalletAddr 해서 usdt jetton wallet 주소 계산
            const jettonMaster = new JettonMaster(masterAddr);
            const masterProvider = client.provider(masterAddr, null);
            const jettonWalletAddr = await jettonMaster.getWalletAddress(masterProvider, ownerAddr);
            console.log('getTokenBalance jettonWalletAddr Non-bounceable :', jettonWalletAddr.toString({ bounceable: false }));
            console.log('getTokenBalance jettonWalletAddr Bounceable :', jettonWalletAddr.toString({ bounceable: true }));

            // 계산된 uset jetton 지갑주소에서 usdt jetton 토큰의 잔액 조회
            const jettonWallet = await JettonWallet.create(jettonWalletAddr);
            const jettonProvider = client.provider(jettonWalletAddr, null);
            const tokenBalance = await jettonWallet.getBalance(jettonProvider)
            console.log('getTokenBalance token balance:', tokenBalance);

            // uset jetton 토큰의 데이터 조회.
            // const jettonData = await jettonMaster.getJettonData(masterProvider);
            // console.log('getTokenBalance jettonData:', jettonData);
            const jettonTokenAddrStr = masterAddr.toString();
            console.log('getTokenBalance jettonTokenAddrStr:', jettonTokenAddrStr);
            
            const metadata = await getTokenDataFromRpc(jettonTokenAddrStr);
            console.log("Token metadata:", metadata);

            const decimals = metadata?.jetton_content?.data?.decimals;
            console.log("Token decimals:", decimals);

            let finalVal = 0;
            // finalVal = !isFormatting ? tokenBalance.toString() : formatTonUnits(tokenBalance, decimals);

            finalVal = Number(tokenBalance.toString())
            console.log(`getTokenBalance finalVal: `, finalVal);

            return {tokenBalance:finalVal, decimals:decimals};
        } catch (error) {
            console.error("getTokenBalance error:", error);
            return {tokenBalance:0, decimals:0};
        }
    };

    /**
     * 서버에서 토큰 잔액 가져오기
     * @param walletAddress 
     * @param mintAddress 
     * @param coinName 
     * @param isFormatting 
     * @returns 
     */
    const getTokenBalanceFromServer = async (
        walletAddress: string,
        mintAddress: string,
        coinName: string,
        isFormatting = true
    ): Promise<number> => {
        const network = await getBlockChain();
        const telegramId = await getTelegramId();
        if (!network || !telegramId) {
            console.warn(`getTokenBalanceFromServer: no network or telegramId`);
            return 0;
        }

        try {
            const response = await axios.post(
            `${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balance`,
            {
                wallet_address: walletAddress,
                network,
                coin_name: coinName,
                tid: telegramId
            }
            );

            if (response.data?.result === true) {
                const rawValue = response.data.data?.ta_total_amount ?? '0';

                if (!isFormatting) {
                    return Number(rawValue.toString());
                }

                // Quicknode TON API를 통해 mintAddress에 해당하는 토큰의 decimal 값을 조회
                const decimals = await getTokenDecimalsFromQuicknode(mintAddress);
                const balance = Number(rawValue.toString()) / Math.pow(10, decimals);
                return balance;
            }

            return 0;
        } catch (error) {
            console.error('getTokenBalanceFromServer error:', error);
            alert('An issue occurred while fetching the token balance from the server');
            return 0;
        }
    };

    /**
     * 서버 코인/토큰 잔액 조회
     * @param walletAddress 
     * @returns 
     */
    const getBalanceListFromServer = async (
        walletAddress: string
    ): Promise<any> => {
        // 파라미터 유효성 검사
        if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
            console.error('유효하지 않은 지갑 주소입니다.');
            return null;
        }
        
        const network = await getBlockChain()
        const telegramId = await getTelegramId()

        try {
            const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/balancelist`, {
                wallet_address: walletAddress,
                network: network,
                coin_name: '',
                tid: telegramId
            });
            
            if (response.data.data && response.data.result === true) {
                console.log(`서버에서 받은 보유수량 리스트 :`, response.data.data)
                return response.data.data;
            }
            
            return null;
        } catch (error) {
            console.error('서버에서 보유수량 리스트 조회 중 오류 발생:', error);
            alert('An issue occurred while fetching the balance list from the server')
            return null;
        }
    }

    /**
     * 추정 가스비
     * @param walletAddress 
     * @returns 
     */
    const estimateGas = async (
        walletAddress: string
    ): Promise<string> => {
        try {
            const currentTime = Date.now();
            const feeTon = '0.05';
    
            gasPriceCache['gasPrice'] = {
                gasFee: feeTon,
                timestamp: currentTime
            };

            setGasPriceCache(gasPriceCache);
            return feeTon;
        } catch (error) {
            console.error('Error estimateGas :', error);
            alert('An issue occurred while estimating the gas fee')
            return '0';
        }
    };

    // ─────────────────────────────────────────────────────────────
    // 5) TON or JETTON 전송
    // ─────────────────────────────────────────────────────────────
    /**
     * 지갑 state, 지갑컨트랙트 배포 상태 확인
     * @param walletAddress 
     * @returns 
     */
    const checkWalletState = async (
        walletAddress: string
    ): Promise<boolean> => {
        try {
            if (!client) {
				console.error('client is not initialized');
				return false;
			}

            // 지갑 information 데이터 요청
            const ownerAddress = Address.parse(walletAddress);
            
            // TonClient를 이용해 지갑 상태를 확인 (컨트랙트가 배포되었는지, active 상태인지)
            const walletState = await client.getContractState(ownerAddress);
            console.log("Wallet state:", walletState);
            
            // wallet information 데이터가 없는 경우
            if (!walletState.state || !walletState.code) {
                return false;
            }

            // 지갑주소 account_state 확인
            // active : 지갑 활성화, code : 데이터 있음 
            // 지갑주소 code 확인
            // code : null 아닌 경우 컨트랙트 배포 상태, null : 지갑 컨트랙트 미배포 상태
            if (walletState.state.toLowerCase() !== 'active') {
                console.warn("지갑 상태가 active가 아닙니다:", walletState.state);
                return false;
            }
            if (!walletState.code) {
                console.warn("지갑 컨트랙트가 배포되지 않았습니다 (code가 없음).");
                return false;
            }

            return true;

        } catch(error) {
            console.error("Error checkWalletState:", error);
        }

        return false;
    }

    /**
     * 지갑 컨트랙트 배포
     * @param walletAddress 
     * @param privateKey 
     * @param publicKey 
     * @returns 
     */
    const deployWalletContract = async (
        walletAddress: string, 
        privateKey: string, 
        publicKey: string
    ): Promise<boolean> => {
        try {
            if (!client) {
				console.error('client is not initialized');
				return false;
			}

            // 지갑 information 데이터 요청
            // client 에서 지갑 컨트랙트 배포 상태 확인
            const isContractDeployed = await client.isContractDeployed(Address.parse(walletAddress));
            console.log("deployWalletContract 지갑 컨트랙트 isDeployed :", isContractDeployed);

            if (true ===isContractDeployed) {
                console.warn("지갑에 컨트랙트가 배포된 상태 입니다 : ", isContractDeployed);
                return true;
            }

            // 지갑 컨트랙트 미배포 상태인 경우 지갑 컨트랙트 배포 처리
            const workchain = 0; // Usually you need a workchain 0
            // hex 문자열을 Buffer로 변환
            const pubKeyBuffer = Buffer.from(publicKey, 'hex');
            const privKeyBuffer = Buffer.from(privateKey, 'hex');
            console.log("deployWalletContract pubKeyBuffer :", pubKeyBuffer);
            console.log("deployWalletContract privKeyBuffer :", privKeyBuffer);

            // Create wallet contract
            let wallet = WalletContractV4.create({ workchain, publicKey: pubKeyBuffer });
            let contract = client.open(wallet);

            // seqno 조회 (미배포 상태라면 보통 0)
            const seqno: number = await contract.getSeqno();
            console.log("deployWalletContract seqno :", seqno);
            
            // 지갑 컨트랙트 배포 메시지 생성 (추가 메시지는 없음)
            let deployMessage = await contract.createTransfer({
                seqno,
                secretKey: privKeyBuffer,  // keyPair 대신 privKeyBuffer 사용
                messages: [],
                sendMode: SendMode.PAY_GAS_SEPARATELY
            });
            console.log("deployWalletContract deployMessage :", deployMessage);

            // 네트워크로 배포 메시지 전송
            await client.sendExternalMessage(wallet, deployMessage);

            // 트랜잭션 컨펌 될 때 까지 대기
            let currentSeqno = seqno;
            let attempts = 0;
            while (currentSeqno === seqno && attempts < 10) { // 10회 이상 반복되면 루프 중지
                await sleep(reqDelay);
                currentSeqno = await contract.getSeqno();
                console.log(`deployWalletContract waiting for transaction to confirm... currentSeqno : ${currentSeqno} === ${seqno} : seqno`);
                attempts++;
            }
            if (attempts >= 15) {
                console.error('트랜잭션 확인 대기 시간 초과');
                return false; // 실패 리턴
            }

            return true;
        } catch (error) {
            console.error('Error deployWalletContract:', error);
            return false;
        }
        return false; // 실패 리턴
    }

    /**
     * 코인, 토큰 전송
     * @param privateKey 
     * @param toAddress 
     * @param amount 
     * @param tokenSymbol 
     * @param tokenAddress 
     * @param publicKey 
     * @returns 
     */
    const sendTransactionWithPrivateKey = async (
        privateKey: string,
        toAddress: string,
        amount: string,
        tokenSymbol: string,
        tokenAddress: string = '',
        publicKey: string = ''
    ): Promise<string> => {

        if (Number(amount) < Number(process.env.REACT_APP_TON_SEND_MIN_AMOUNT)) {
            console.error(`전송 최소 수량인 ${process.env.REACT_APP_TON_SEND_MIN_AMOUNT} 이하 전송 : ${amount}`);
            const alertMessage = t('template_withdraw_screen_message_36') + ' ' + process.env.REACT_APP_TON_SEND_MIN_AMOUNT;
            alert(alertMessage);
            return '';
        }

        try {
            const mainCurrency = tonNetwork?.currency || 'ton'
            if (tokenSymbol.toLowerCase() === (mainCurrency || 'ton').toLowerCase()) {
                // TON 전송
                return await sendTonCoin(privateKey, toAddress, amount, publicKey);
            } else {
                // JETTON 토큰
                return await sendJettonTokenV1(privateKey, toAddress, amount, tokenAddress, publicKey);
            }
        } catch (error) {
            console.error('TON sendTransactionWithPrivateKey error:', error);
            alert('An issue occurred while sending the transaction')
            throw error;
        }
    };

    /**
     * TON 코인 전송
     * @param privateKey 
     * @param toAddress 
     * @param amount 
     * @param publicKey 
     * @returns 
     */
    const sendTonCoin = 
    async (
        privateKey: string, 
        toAddress: string, 
        amount: string, 
        publicKey: string
    ): Promise<string> => {
        try {
            if (!client) {
				console.error('client is not initialized');
				return '';
			}

            // 지갑 state 상태, 지갑 컨트랙트 배포 확인
            const walletAddress = await getWalletAddress()
            
            // 지갑 활성 상태 확인
            const isActive = await checkWalletState(walletAddress)
            if (false === isActive) {
                console.warn("지갑 상태가 uninitialized 상태 입니다:", isActive);
                const isDeployed = await deployWalletContract(walletAddress, privateKey, publicKey)
                if (false === isDeployed) {
                    console.warn("지갑 컨트랙트 배포 실패:", isDeployed);
                    return '';
                }
                console.info("지갑 컨트랙트 배포 성공:", isDeployed);
            } else {
                console.info("지갑 활성화 상태 : ", isActive);
            }

            const sendAmount = toNano(amount);
            console.log("sendTonCoin sendAmount toNano :", sendAmount);

            // 지갑 컨트랙트 배포 상태인 경우 트랜잭션 전송
            const walletAddressParsed = Address.parse(walletAddress); // 지갑 주소 파싱
            console.log("sendTonCoin walletAddressParsed :", walletAddressParsed);
            console.log("sendTonCoin 보낼 지갑주소 :", toAddress);

            const workchain = 0; // 일반적으로 workchain은 0
            const wallet = WalletContractV4.create({ workchain, publicKey: Buffer.from(publicKey, 'hex') });
            const contract = client.open(wallet);

            // 트랜잭션 seqno 조회
            const seqno = await contract.getSeqno();
            console.log("sendTonCoin seqno :", seqno);

            // 트랜잭션 메시지 생성
            const transferMessage = await contract.createTransfer({
                seqno,
                secretKey: Buffer.from(privateKey, 'hex'), // 개인키를 Buffer로 변환
                messages: [
                    internal({
                        value: sendAmount, // 전송할 금액
                        to: toAddress, // 수신자의 TON 주소
                        body: '', // 메시지 바디
                    })
                ],
                sendMode: SendMode.PAY_GAS_SEPARATELY,
            });
            
            // 네트워크로 트랜잭션 전송
            await client.sendExternalMessage(wallet, transferMessage);
            
            // 마지막 트랜잭션 정보
            const lastTxResult = await getWalletLastTxHashFromRpc(walletAddress);
            console.log('sendTonCoin lastTxResulttxResult : ', lastTxResult);
            const lastTxHash = lastTxResult.txHash;
            console.log('sendTonCoin txResult lastTxHash : ', lastTxHash);

            // 트랜잭션 컨펌 될 때 까지 대기
            let attempts = 0;
            let currentTxHash = lastTxHash;
            while (lastTxHash === currentTxHash && attempts < 10) { // 10회 이상 반복되면 루프 중지
                await sleep(reqDelay);
                const currentTx = await getWalletLastTxHashFromRpc(walletAddress);
                currentTxHash = currentTx.txHash;
                console.log(`waiting for transaction to confirm... currentTxHash : ${currentTxHash} === ${lastTxHash} : lastTxHash`);
                attempts++;
            }
            if (attempts >= 10) {
                console.error('트랜잭션 확인 대기 시간 초과');
                return ''; // 실패 리턴
            }

            console.log('sendTonCoin send txHash : ', currentTxHash);

            // seqNo가 일치하면 리턴
            return currentTxHash;
        } catch (error) {
            console.error('sendTonCoin error:', error);
            alert('An issue occurred while sending the TON coin transaction')
            return '';
        }
    };
    
    /**
     * jetton 토큰 전송
     * @param fromSecretKey 
     * @param toAddress 
     * @param amount 
     * @param mintAddress 
     * @param publicKey 
     * @returns 
     */
    const sendJettonTokenV1 = 
    async (
        fromSecretKey: string,
        toAddress: string,   // 수신자(상대방) 지갑 주소 (메인 TON 지갑)
        amount: string,      // 예: "0.2" (USDT 등 Jetton 토큰 수량, 소수점)
        mintAddress: string, // Jetton Minter 주소 (예: USDT 마스터 컨트랙트)
        publicKey: string
    ): Promise<string> => {
        try {
            if (!client) {
				console.error('client is not initialized');
				return '';
			}

            // 1. 내 메인 TON 지갑 주소 (string)
            const walletAddress = await getWalletAddress(); // 프로젝트 로직에 맞게 구현
            console.log('1. sendJettonTokenV1 메인 지갑 주소 : ', walletAddress);
    
            // 2. TON 지갑 상태 확인 → 미배포 시 배포
            const isActive = await checkWalletState(walletAddress);
            if (!isActive) {
                console.warn("2.1 sendJettonTokenV1 메인 지갑 상태가 uninitialized 상태입니다.");
                const isDeployed = await deployWalletContract(walletAddress, fromSecretKey, publicKey);
                if (!isDeployed) {
                    console.warn("2.2 sendJettonTokenV1 메인 지갑 컨트랙트 배포 실패");
                    return '';
                }
                console.info("2.3 sendJettonTokenV1 메인 지갑 컨트랙트 배포 성공");
            } else {
                console.info("2.4 sendJettonTokenV1 메인 지갑 활성화 상태:", isActive);
            }
    
            // 3. Jetton Wallet 주소 계산
            const masterAddr = Address.parse(mintAddress);
            const ownerAddr = Address.parse(walletAddress);
            const jettonMaster = new JettonMaster(masterAddr);
            const masterProvider = client.provider(masterAddr, null);

            // 해당 토큰의 jetton wallet 주소 계산
            const jettonWalletAddr = await jettonMaster.getWalletAddress(masterProvider, ownerAddr);
            console.log('3.1 sendJettonTokenV1 Jetton Wallet Address (non-bounceable) :', jettonWalletAddr.toString({ bounceable: false }));
            console.log('3.2 sendJettonTokenV1 Jetton Wallet Address (bounceable) :', jettonWalletAddr.toString({ bounceable: true }));
    
            // 4. Wallet conteact 인스턴스 생성
            const workchain = 0; // 일반적으로 workchain은 0
            const wallet = WalletContractV4.create({ workchain, publicKey: Buffer.from(publicKey, 'hex') });
            const contract = client.open(wallet);
    
            // 5. Jetton Decimals 파악
            // 토큰 메타데이터 정보 요청
            const jettonTokenAddrStr = masterAddr.toString();
            const metadata = await getTokenDataFromRpc(jettonTokenAddrStr);
            console.log("4.1 sendJettonTokenV1 전송할 토큰의 metadata :", metadata);
            const decimals = metadata?.jetton_content?.data?.decimals;
            console.log("4.2 sendJettonTokenV1 전송할 토큰의 Decimals :", decimals);
            
            // 토큰 전송 수량 계산
            const tokenBigInt = BigInt(Math.floor(parseFloat(amount) * 10 ** decimals));
            console.log("4.3sendJettonTokenV1  전송할 토큰 수량 :", tokenBigInt);

            // 6. 트랜잭션 seqno 조회
            const seqno = await contract.getSeqno();
            console.log("4.4 sendJettonTokenV1 seqno :", seqno);

            // 7. token 전송 transferMessage 메시지 생성
            const feeTon = await estimateGas(walletAddress);
            const baseJettonSendFee = toNano(feeTon); // Base gas fee required for the jetton transfer
            console.log("4.5 sendJettonTokenV1 baseJettonSendFee :", baseJettonSendFee);

            // jetton 토큰 전송 메시지 payload 생성
            const toAddr = Address.parse(toAddress);
            console.log(`5.1 sendJettonTokenV1 토큰 보낼 주소 ${toAddress} | Address.parse : `, toAddr);

            const jettonTransferPayload = beginCell()
                .storeUint(0xf8a7ea5, 32) // JETTON_TRANSFER_OP_CODE (operation identifier)
                .storeUint(0, 64) // Query ID (0 for new transactions)
                .storeCoins(tokenBigInt) // Amount to transfer (1 USDt)
                .storeAddress(toAddr) // Recipient address
                .storeAddress(wallet.address) // Address to receive excess funds (usually address of sender)
                .storeBit(false) // No custom payload
                .storeCoins(1n) // Forward fee in nanoTON (for send notify to wallet)
                .storeMaybeRef(undefined)
                .endCell();
            
            console.log(`5.2 sendJettonTokenV1 토큰 전송 페이로드 : `, jettonTransferPayload);

            const transferMessage = await contract.createTransfer({
                seqno, // Required to ensure transaction uniqueness
                secretKey: Buffer.from(fromSecretKey, 'hex'), // Sign the transaction with the private key
                sendMode: SendMode.PAY_GAS_SEPARATELY + SendMode.IGNORE_ERRORS, // Specify send mode
                messages: [
                    internal({
                        to: jettonWalletAddr, // 내 jetton 토큰 지갑주소
                        value: baseJettonSendFee, // Gas fee. 토큰 지갑에 전송된다
                        bounce: true,
                        body: jettonTransferPayload // Jetton transfer payload
                    })
                ]
            });
            console.log(`5.3 sendJettonTokenV1 전송 메시지 : `, transferMessage);
            console.log(`5.4 sendJettonTokenV1 전송 메시지 txHash base64 : `, transferMessage.hash().toString('base64'));
            console.log(`5.5 sendJettonTokenV1 전송 메시지 txHash hex : `, transferMessage.hash().toString('hex'));

            // 네트워크로 트랜잭션 전송
            await client.sendExternalMessage(wallet, transferMessage);

            // 마지막 트랜잭션 정보
            const lastTxResult = await getWalletLastTxHashFromRpc(walletAddress);
            console.log('sendJettonTokenV1 lastTxResulttxResult : ', lastTxResult);
            const lastTxHash = lastTxResult.txHash;
            console.log('sendJettonTokenV1 txResult lastTxHash : ', lastTxHash);

            // 트랜잭션 컨펌 될 때 까지 대기
            let attempts = 0;
            let currentTxHash = lastTxHash;
            while (lastTxHash === currentTxHash && attempts < 10) { // 10회 이상 반복되면 루프 중지
                await sleep(reqDelay);
                const currentTx = await getWalletLastTxHashFromRpc(walletAddress);
                currentTxHash = currentTx.txHash;
                console.log(`waiting for jetton transaction to confirm... currentTxHash : ${currentTxHash} === ${lastTxHash} : lastTxHash`);
                attempts++;
            }
            if (attempts >= 10) {
                console.error('트랜잭션 확인 대기 시간 초과');
                return ''; // 실패 리턴
            }

            console.log('sendJettonTokenV1 send token txHash : ', currentTxHash);

            // 마지막 TX 리턴
            return currentTxHash;
        } catch (error) {
            console.error('sendJettonTokenV1 error:', error);
            alert('토큰 전송 중 문제가 발생했습니다.');
                return '';
        }
    };


    /**
   * TON 네트워크에서 네이티브 트랜잭션(일반+내부)을 모두 조회
   * @param walletAddress
   * @param order 정렬 asc / desc (기본값 asc)
   * @returns { signature: string, slot: number }[]
   */
    const getAllNativeTxForAddressFromRpc = async (
        walletAddress: string,
        order: string = 'asc',
        waitTime=reqDelay,
    ): Promise<{ 
        signature: string;
        slot: number;
        mintAddress: string;
        txType: 'normal' | 'internal';
        hash?: string;
        from?: string;
        to?: string;
        value?: string;
        timeStamp?: string;
        nonce?: string;
        blockHash?: string;
        transactionIndex?: string;
        gas?: string;
        gasPrice?: string;
        isError?: string;
        txreceipt_status?: string;
        input?: string;
        contractAddress?: string;
        cumulativeGasUsed?: string;
        gasUsed?: string;
        confirmations?: string;
        methodId?: string;
        functionName?: string;
        traceId?: string;      // internal tx 전용 필드
        type?: string;         // internal tx에서 "call" 등
        errCode?: string;      // internal tx에서 에러코드
    }[]> => {
        try {
            // 1) indexer api 트랜잭션
            const firstTxs = await getTrasactionsFromIndexerAPI(walletAddress, 0, 100);
            console.log(`1 getAllNativeTxForAddressFromRpc firstTxs:`, firstTxs);

            // 2) 트랜잭션이 100개인 경우 다음페이지 트랜잭션 요청
            let allTxs = [...firstTxs];
            let offset = 100;
            
            // console.log(`2 getAllNativeTxForAddressFromRpc allTxs.length:`, allTxs.length);

            while (allTxs.length % 100 === 0 && allTxs.length > 0) {
                const nextTxs = await getTrasactionsFromIndexerAPI(walletAddress, offset, 100);
                console.log(`3-1 getAllNativeTxForAddressFromRpc nextTxs:`, nextTxs);
                
                if (!nextTxs || nextTxs.length === 0){
                    console.log(`3-2 getAllNativeTxForAddressFromRpc 페이징 요청 중지:`);
                    break;
                }
                
                allTxs = [...allTxs, ...nextTxs];
                offset += 100;
                
                // API 과부하 방지를 위한 딜레이
                await sleep(waitTime);
            }
            console.log(`3-3 getAllNativeTxForAddressFromRpc allTxs : `, allTxs);

            // 8) 중복제거 하지 않고 정렬
            // 같은 tx hash 내부에서 코인, 토큰이 동시에 변경되는 경우가 있을 수 있음
            let sortedTxs: any[] = [];
            if (order === 'asc') {
                sortedTxs = allTxs.sort((a, b) => allTxs.indexOf(a) - allTxs.indexOf(b));
            } else {
                sortedTxs = allTxs.sort((a, b) => allTxs.indexOf(b) - allTxs.indexOf(a));
            }

            // console.log(`4-1 getAllNativeTxIdForAddressFromRpc sortedTxs:`, sortedTxs)
            console.log(`4-2 getAllNativeTxForAddressFromRpc sortedTxs.length:`, sortedTxs.length);

            // 9) 변환
            // sortedTxs의 각 요소를 1:1로 매핑하므로 결과 배열의 길이는 동일합니다
            let txList: any[] = sortedTxs.map((tx) => {
                
                const hexHash = convertHashTpHex(tx.hash);

                // 기본 객체 생성
                const result = {
                    signature: hexHash,
                    slot: tx.lt,
                    mintAddress: '', // native tx는 mintAddress가 없음
                    txType: 'normal',

                    // normal tx 필드
                    hash: hexHash,
                    from: '',
                    to: '',
                    value: '0',
                    timeStamp: tx.now,
                    nonce: '',
                    blockHash: tx.mc_block_seqno,
                    transactionIndex: tx.trace_id,
                    gas: '0',
                    gasPrice: '0',
                    isError: '',
                    txreceipt_status: '',
                    input: '',
                    contractAddress: '',
                    cumulativeGasUsed: '',
                    gasUsed: '0',
                    confirmations: '',
                    methodId: '',
                    functionName: '',

                    // internal tx 필드
                    traceId: tx.trace_id, // e.g. "0_1"
                    type: '',       // e.g. "call"
                    errCode: '', // e.g. ""
                };

                // console.log(`5-1 getAllNativeTxForAddressFromRpc tx:`, tx);

                // 조건에 따라 분기 처리

                // 트랜잭션 실행 상태 확인

                // 입금, 출금 트랜잭션 구분
                // ---- (A) 기본 값 추출
                
                const inMsg = tx.in_msg; // in_msg
                const outMsgs = tx.out_msgs || []; // out_msgs

                // ---- (B) TON 코인 수량
                // 보통 value는 nanoTON 단위 (문자열) -> Number 변환
                // ex) "47056795" (약 0.047056795 TON)
                // 필요하다면 fromNano 변환 가능
                let inMsgValue = 0;
                if (inMsg && inMsg.value) {
                    inMsgValue = Number(inMsg.value);
                }
            
                // out_msg가 여러 개일 경우 합산 / 혹은 하나씩 분석
                // 여기서는 첫 번째 out_msg.value만 간단히 예시로 사용
                let outMsgValue = 0;
                if (outMsgs.length > 0 && outMsgs[0].value) {
                    outMsgValue = Number(outMsgs[0].value);
                }

                const hexWalletAddress = convertWalletAddressToHex(walletAddress)

                // 수량 차이 계산 (account_state_after - account_state_before)
                const beforeBalance = tx.account_state_before?.balance ? Number(tx.account_state_before.balance) : 0;
                const afterBalance = tx.account_state_after?.balance ? Number(tx.account_state_after.balance) : 0;
                const diffAmount = afterBalance - beforeBalance;

                console.log(`6 getAllNativeTxIdForAddressFromRpc diffAmount:`, diffAmount);
                
                 // ---- (C) 입금/출금 여부 판별
                // [입금 조건 예시]
                // 1) diffAmount > 0
                // [입금]
                if (diffAmount > 0)
                {
                    result.type = "receive";
                    result.from = convertHexToWalletAddress(inMsg.source);
                    result.to = convertHexToWalletAddress(inMsg.destination);
                    
                    // 수수료 제외한 실제 입금 수량(befre - after 변경된 수량)
                    result.value = (Number(diffAmount)).toString();
                    result.gasUsed = '0';

                    console.log(`7-1 getAllNativeTxIdForAddressFromRpc 입금 tx :`, tx);
                    console.log(`7-2 getAllNativeTxIdForAddressFromRpc 입금 result :`, result);

                    return result;
                }
                // [출금 조건 예시]
                // 1) in_msg.source 가 null/"" (지갑 내부 컨트랙트 호출)
                // 2) out_msgs 배열이 있고, 그 중에 내 지갑주소가 source 인 메시지가 있으며, value > 0
                else if(diffAmount < 0 && outMsgs.length > 0){
                    // 보통 첫번째 out_msg를 보고 '출금'으로 판단
                    // 혹은 outMsgs.find(...) 로 내 지갑 address가 source 인지 확인
                    const firstOut = outMsgs[0];
                    console.log(`8-1 getAllNativeTxIdForAddressFromRpc firstOut:`, firstOut);

                    // 출금
                    const withdrawalAmount = Math.abs(diffAmount);
                    
                    result.type = "withdrawal";
                    result.from = convertHexToWalletAddress(firstOut.source);
                    result.to = convertHexToWalletAddress(firstOut.destination || "");

                    // 변경 수량에서  수수료 차감
                    result.value = (Number(withdrawalAmount) - Number(tx.total_fees)).toString();
                    result.gasUsed = tx.total_fees;

                    console.log(`8-2 getAllNativeTxForAddressFromRpc 출금 tx:`, tx);
                    console.log(`8-3 getAllNativeTxIdForAddressFromRpc 출금 result:`, result);

                    return result;

                } else {
                    // 출금
                    const withdrawalAmount = Math.abs(diffAmount);

                    // 출금인데 out이 없는 경우 수량 변경 내용만 추가
                    result.type = "withdrawal";
                    result.from = convertHexToWalletAddress(inMsg.source || "");
                    result.to = convertHexToWalletAddress(inMsg.destination);

                    // 수정전
                    result.value = '0';
                    result.gasUsed = withdrawalAmount.toString();

                    // result.value = withdrawalAmount.toString();
                    // result.value = '0';
                    // result.gasUsed = withdrawalAmount.toString();
                    // result.gasUsed = tx.total_fees;

                    // 변경
                    // result.value = withdrawalAmount.toString();
                    // result.gasUsed = '0';

                    console.log(`9-1 getAllNativeTxForAddressFromRpc 출금 2 tx:`, tx);
                    console.log(`9-2 getAllNativeTxIdForAddressFromRpc 출금 2 result :`, result);

                    return result;
                }
            });
            // txIdList.length === sortedTxs.length가 보장됩니다
    
            console.log(`6 getAllNativeTxIdForAddressFromRpc 전체 tx 수 : ${txList.length}`, txList);
            return txList;
        } catch (error) {
            console.error('getAllNativeTxIdForAddressFromRpc error:', error);
            return [];
        }
    };

    /**
   * ton rpc 서버를 통해서 
   * @param walletAddress 지갑 주소 (0x...)
   * @param contractAddress BEP-20 토큰 컨트랙트 주소 (0x...)
   * @param order 결과 정렬 (asc | desc), 기본 'asc'
   * @returns { signature: string; slot: number }[] (signature=트랜잭션 해시, slot=블록 번호)
   */
    const getAllJettonTxIdForAddressFromRpc = async (
        walletAddress: string,
        contractAddress: string,
        order: string = 'asc'
    ): Promise<{ 
        signature: string; 
        slot: number; 
        mintAddress: string 
        txType: 'normal' | 'internal';
    
        // 추가 필드
        hash?: string;
        timeStamp?: string;
        nonce?: string;
        blockHash?: string;
        from?: string;
        to?: string;
        value?: string;
        tokenName?: string;
        tokenSymbol?: string;
        tokenDecimal?: string;
        transactionIndex?: string;
        gas?: string;
        gasPrice?: string;
        gasUsed?: string;
        cumulativeGasUsed?: string;
        input?: string;
        confirmations?: string;
        decimals?: string;
    }[]> => {
        try {
            if (!client) {
				console.error('client is not initialized');
				return [];
			}

            // 3. Jetton Wallet 주소 계산
            const masterAddr = Address.parse(contractAddress);
            const ownerAddr = Address.parse(walletAddress);
            const jettonMaster = new JettonMaster(masterAddr);
            const masterProvider = client.provider(masterAddr, null);

            // 해당 토큰의 jetton wallet 주소 계산
            const jettonWallet = await jettonMaster.getWalletAddress(masterProvider, ownerAddr);
            // const jettonWalletAddr = jettonWallet.toString({ bounceable: false });
            console.log('1-1 getAllJettonTxIdForAddressFromRpc Jetton Wallet Address (non-bounceable) :', jettonWallet.toString({ bounceable: false }));
            console.log('1-2 getAllJettonTxIdForAddressFromRpc Jetton Wallet Address (bounceable) :', jettonWallet.toString({ bounceable: true }));

            // 1) indexer api 트랜잭션
            const firstTxs = await getTokenTransferDataFromIndexerAPI(walletAddress, contractAddress, 0, 100);
            console.log(`2-1 getAllJettonTxIdForAddressFromRpc firstTxs:`, firstTxs);

            // 2) 트랜잭션이 100개인 경우 다음페이지 트랜잭션 요청
            let allTxs = [...firstTxs];
            let offset = 100;
            
            console.log(`2-2 getAllJettonTxIdForAddressFromRpc allTxs.length:`, allTxs.length);

            while (allTxs.length % 100 === 0 && allTxs.length > 0) {
                const nextTxs = await getTokenTransferDataFromIndexerAPI(walletAddress, contractAddress, offset, 100);
                console.log(`3-1 getAllJettonTxIdForAddressFromRpc nextTxs:`, nextTxs);
                
                if (!nextTxs || nextTxs.length === 0){
                    console.log(`3-2 getAllJettonTxIdForAddressFromRpc 페이징 요청 중지:`);
                    break;
                }
                
                allTxs = [...allTxs, ...nextTxs];
                offset += 100;
                
                // API 과부하 방지를 위한 딜레이
                await sleep(1500);
            }
            console.log(`3-3 getAllJettonTxIdForAddressFromRpc allTxs : `, allTxs);

             // 8) 중복제거 하지 않고 정렬
            // 같은 tx hash 내부에서 코인, 토큰이 동시에 변경되는 경우가 있을 수 있음
            let sortedTxs: any[] = [];
            if (order === 'asc') {
                sortedTxs = allTxs.sort((a, b) => allTxs.indexOf(a) - allTxs.indexOf(b));
            } else {
                sortedTxs = allTxs.sort((a, b) => allTxs.indexOf(b) - allTxs.indexOf(a));
            }

            console.log(`4 getAllJettonTxIdForAddressFromRpc sortedTxs:`, sortedTxs)

            const tokenDecimals = await getTokenDecimalsFromQuicknode(contractAddress);
            const networkName = tonNetwork?.symbol.toLowerCase() || 'ton'
            const tokenInfo = await getNetworkTokenDataByAddress(networkName.toLowerCase(), contractAddress);
            console.log(`5. getAllJettonTxIdForAddressFromRpc tokenInfo : `, tokenInfo);

            // 9) 변환
            // sortedTxs의 각 요소를 1:1로 매핑하므로 결과 배열의 길이는 동일합니다
            let txIdList: any[] = sortedTxs.map((tx) => {

                const hexHash = convertHashTpHex(tx.transaction_hash);

                // 기본 객체 생성
                const result = {
                    signature: hexHash,
                    slot: tx.transaction_lt,
                    mintAddress: contractAddress, // native tx는 mintAddress가 없음
                    txType: 'normal',

                    // normal tx 필드
                    hash: hexHash,
                    timeStamp: tx.transaction_now,
                    nonce: tx.mc_block_seqno,
                    blockHash: '',
                    from: '',
                    to: '',
                    value: '',
                    tokenName: tokenInfo.name.toLowerCase(),
                    tokenSymbol: tokenInfo.symbol.toLowerCase(),
                    tokenDecimal: '',
                    transactionIndex: tx.trace_id,
                    gas: '0',
                    gasPrice: '0',
                    isError: '',
                    txreceipt_status: tx.txreceipt_status,
                    input: '',
                    contractAddress: contractAddress,
                    cumulativeGasUsed: '',
                    gasUsed: '0',
                    confirmations: '',
                    methodId: '',
                    functionName: '',
                    decimals: '',

                    // internal tx 필드
                    traceId: tx.trace_id, // e.g. "0_1"
                    type: '',       // e.g. "call"
                    errCode: '', // e.g. ""
                };

                const hexWalletAddress = convertWalletAddressToHex(walletAddress)
                const from = convertHexToWalletAddress(tx.source)
                const to = convertHexToWalletAddress(tx.destination)
                
                if(hexWalletAddress.toLowerCase() === tx.source.toLowerCase()) {
                    // 출금
                    result.type = 'withdrawal';
                    result.from = from;
                    result.to = to;
                    result.value = tx.amount;
                    result.decimals = tokenDecimals.toString();
                } else {
                    // 입금
                    result.type = 'receive';
                    result.from = from;
                    result.to = to;
                    result.value = tx.amount;
                    result.decimals = tokenDecimals.toString();
                }

                return result;
            });
            // txIdList.length === sortedTxs.length가 보장됩니다
    
            console.log(`5 getAllNativeTxIdForAddressFromRpc 전체 tx 수(일반+내부): ${txIdList.length}`, txIdList);
            return txIdList;
        } catch (error) {
            console.error('getAllJettonTxIdForAddressFromRpc error:', error);
            return [];
        }
    };

    /**
     * 지갑주소 총 트랜잭션 수 조회
     * @param walletAddress 
     * @returns 
     */
    const getTotalTxCount = async (
        walletAddress: string
    ) => {
        try {
            // 지갑의 아웃바운드 txCount (nonce)만 반환
            // const txCount = await provider.getTransactionCount(walletAddress);
            const txCount = 0
            return txCount;
        } catch (error) {
            console.error('getTotalTxCount error:', error);
            return 0;
        }
    };

    /**
     * 메인지갑 트랜잭션 수 조회
     * @param walletAddress 
     * @param limit 
     * @returns 
     */
    // const getLimitTxCount = async (walletAddress: string, limit: number = 1000) => {
    //   try {
    //     // 단순히 getTxIdForAddress(..., limit) 호출 후 길이 반환
    //     const txList = await getAllNativeTxIdForAddress(walletAddress, 'asc');
    //     return txList.length;
    //   } catch (error) {
    //     console.error('getLimitTxCount error:', error);
    //     return 0;
    //   }
    // };

    /**
     * 로컬스토리지에 저장된 네트워크 토큰 데이터 조회
     * @param forceUpdate 
     * @returns 
     */
    const getNetworkTokenData = async (
        network:string
    ): Promise<any> => {
        const tokenDataKey = 'tokenData';
        const tokenInfo = localStorage.getItem(tokenDataKey);
        
        let networkTokens = [];
        if (tokenInfo && tokenInfo !== null && tokenInfo !== undefined && tokenInfo !== '') {
            networkTokens = JSON.parse(tokenInfo).filter((token:any) => {
            return (token.networkname.toLowerCase() === network.toLowerCase() 
            && token.type === 'token' 
            && token.contract && token.contract !== '');
            });
        }
            console.log(`TonContext getNetworkTokenData network:${network} count:${networkTokens.length}`, networkTokens);
        
        return networkTokens;
    };

    /**
     * 토큰 주소로 로컬 스토리지의 네트워크 토큰 정보 검색
     */
    const getNetworkTokenDataByAddress = async (
        network:string, 
        tokenAddress:string
    ): Promise<any> => {
        const tokenDataKey = 'tokenData';
        const tokenInfo = localStorage.getItem(tokenDataKey);
        
        let networkToken = null;
        if (tokenInfo && tokenInfo !== null && tokenInfo !== undefined && tokenInfo !== '') {
            const networkTokens = JSON.parse(tokenInfo).filter((token:any) => {
            return (token.networkname.toLowerCase() === network.toLowerCase() 
            && token.type === 'token' 
            && token.contract && token.contract.toLowerCase() === tokenAddress.toLowerCase());
            });
            if (networkTokens.length > 0) {
            networkToken = networkTokens[0];
            }
        }
            console.log(`TonContext getNetworkTokenDataByAddress network:${network} count:${networkToken ? 1 : 0}`, networkToken);
        
        return networkToken;
    };

    // 중지 flag
    const stopRequestTxData = useCallback((isStop: boolean = true) => {
        console.log('stopRequestTxData =>', isStop);
        setReqTxStop(isStop);
    }, []);

    /**
       * 백그라운드 DB 현행화
       * @param walletAddress 
       * @param limit 
       * @returns 
      **/
    const syncAllTransaction = async (walletAddress: string) => {
        if (runAllTransactionHistoryRef.current) {
            console.warn('이미 syncAllTransactionHistory 실행 중');
            return true;
        }
        const selNetwork = await getBlockChain();
        const telegramId = await getTelegramId();

        if (!selNetwork) {
            console.error('syncAllTransaction: no network');
            return false;
        }
        if (!telegramId) {
            console.error('syncAllTransaction: no telegramId');
            return false;
        }

        try {
            // 동작중 flag 변경
            setIsRunAllTransactionHistory(true);

            // stop state reset
            setReqTxStop(false);

            // DB TX 동기화 정보 표시 초기화
            setCountSyncProcess(0) // 진행한 갯수
            setCountSyncComplete(0) // 전체 갯수
            setLastSyncTxs([]) // 동기화 TX 전송내역

            setSyncStatus(SYNC_STATUS.CHECKING);

            // syncKey 생성
            const randomNumbers = Array.from({ length: 6 }, () => Math.floor(Math.random() * 10));
            const key = `sync${Date.now()}${randomNumbers.join('')}`;
            setSyncKey(key);

            // ton 네트워크의 토큰 정보
            const networkTokens = await getNetworkTokenData(selNetwork);
            console.log(`1-1 syncAllTransactionHistory ${selNetwork} networkTokens : `, networkTokens);

            // TonScan 에서 모든 네이티브 TX ID 가져오기
            let rpcTxList = await getAllNativeTxForAddressFromRpc(walletAddress, 'asc');
            console.log(`1-2 syncAllTransactionHistory walletAddress : ${walletAddress} rpcTxList : `, rpcTxList);

            // RPC 에서 토큰 TX 가져오기
            for (const token of networkTokens) {
                // 지정한 토큰의 txid 조회
                const tokenTxList = await getAllJettonTxIdForAddressFromRpc(walletAddress, token.contract, 'asc');
                console.log(`1-3 syncAllTransactionHistory walletAddress : ${walletAddress}  token : ${token.symbol} contract : ${token.contract} tokenTxList : `, tokenTxList);

                // tokenTxList를 tokenRpcTxIds에 추가
                if (tokenTxList && tokenTxList.length > 0) {
                    rpcTxList = [...rpcTxList, ...tokenTxList];
                }
            
                // reqTxStop 체크
                if (reqTxStopRef.current) {
                    setSyncStatus(SYNC_STATUS.STOP);
                    return true;
                }
            }
            console.log(`1-4 syncAllTransactionHistory RPC coin rpcTxList : `, rpcTxList);

            if (!rpcTxList || rpcTxList.length === 0) {
                console.log('rpcTxList 데이터가 없습니다. syncAllTransactionHistory 함수를 종료합니다.');
                setSyncStatus(SYNC_STATUS.COMPLETE);
                return true;
            }

            // slot 번호 기준으로 오름차순 정렬
            rpcTxList.sort((a, b) => {
                if (a.slot && b.slot) {return a.slot - b.slot;}
                return 0;
            });
            console.log(`1-5 syncAllTransactionHistory slot 정렬된 rpcTxIds : `, rpcTxList);


            // RPC 에서 보유한 코인 balance 정보를 가져온다
            const rpcCoinBalance = await getBalance(walletAddress, false);
            console.log(`2 syncAllTransactionHistory RPC coinBalance : `, rpcCoinBalance);

            // RPC 지정한 토큰 컨트랙트의 잔액 정보를 가져오기
            let tokenBalances: any[] = [];
            // 토큰 컨트랙트 별 잔액 정보 가져오기
            for (const token of networkTokens) {
                const balance = await getTokenBalance(walletAddress, token.contract, false);
                tokenBalances.push({ mintAddress: token.contract, balance: balance });
            }
            console.log(`3 syncAllTransactionHistory RPC tokenBalances : `, tokenBalances);

            // 서버에서 최신 tx id 데이터 10개를 가져온다
            const serverTxIds = await getServerTxIdList(walletAddress, selNetwork, telegramId, 1, 10);
            console.log(`4 syncAllTransactionHistory serverTxIds : `, serverTxIds);

            // 서버에서 코인 보유수량 리스트를 가져온다
            const mainCurrency = tonNetwork?.currency.toLowerCase() || 'ton'
            const serverBalances = await getBalanceFromServer(walletAddress, mainCurrency, false);

            console.log(`5 syncAllTransactionHistory server Coin Balances : `, serverBalances);

            // 서버에서 토큰 보유수량 리스트를 가져온다
            const serverTokenBalances = await getBalanceListFromServer(walletAddress)
            console.log(`6 syncAllTransactionHistory server Token Balances : `, serverTokenBalances);

            // rpc - 서버 코인 수량 비교
            let rpcCoinMatch = false;
            if (rpcCoinBalance.toString() === serverBalances.toString()) {
                console.log('RPC와 서버의 코인 잔액이 일치합니다.');
                rpcCoinMatch = true;
            } else {
                console.warn(`RPC와 서버의 코인 잔액이 일치하지 않습니다. RPC: ${rpcCoinBalance}, 서버: ${serverBalances}`);
            }

            // rpc - 서버 토큰 비교
            const matchingTokens = tokenBalances.filter(token =>
                serverTokenBalances.some((serverToken: any) => serverToken.ta_mintaddress === token.mintAddress)
            )
            console.log('7-1 syncAllTransactionHistory 지갑서버 토큰 정보:', matchingTokens)
            console.log('7-2 syncAllTransactionHistory 일치하는 토큰 정보:', matchingTokens)

            // (일치하는 TX 있는지 비교) - 간단화
            // rpc의 최신 tx id 1개와 서버에서 가져온 최신 tx id 1개가 일치하는지 확인한다
            let rpcTxIdMatch = false;
            if (rpcTxList != null && serverTxIds != null && rpcTxList.length !== 0 && serverTxIds.length !== 0) {
                const rpcLatestTxId = rpcTxList[rpcTxList.length - 1];
                const serverLatestTxId = serverTxIds[0].transaction_hash;	
                
                if (rpcLatestTxId?.signature?.toString() === serverLatestTxId?.toString()) {
                    console.log('RPC의 최신 tx id와 서버의 최신 tx id가 일치합니다.');
                    rpcTxIdMatch = true;
                }
            }
    
            let rpcTokensMatch = true;
            if (!serverTokenBalances || serverTokenBalances.length === 0) {
                console.warn('서버의 토큰 보유수량 데이터가 없습니다.');
                rpcTokensMatch = false;
            } else if(matchingTokens.length > 0) {
                for (const token of matchingTokens) {
                const serverToken = serverTokenBalances.find((st: any) => st?.ta_mintaddress?.toString() === token.mintAddress.toString());
    
                console.log(`rpc 토큰 정보 :`, token);
                console.log(`서버 토큰 정보 :`, serverToken);
    
                console.log(`RPC - 서버 토큰 보유수량 비교 ${serverToken.ta_total_amount} | ${token.balance}`);
                if (!serverToken || token.balance.toString() !== serverToken.ta_total_amount.toString()) {
                    console.warn(`토큰 ${token.mintAddress}의 잔액 ${token.balance} - ${serverToken.ta_total_amount} 이 일치하지 않습니다.`);
                    rpcTokensMatch = false;
                    break;
                }
                }
            } else {
                rpcTokensMatch = false;
                console.log(`rpc 토큰 정보 일치하지 않음 rpcTokensMatch : `, rpcTokensMatch);
            }
    
            if (rpcTxIdMatch && rpcCoinMatch && rpcTokensMatch) {
                console.log('RPC의 최신 tx id와 서버의 최신 tx id가 일치하며 코인, 토큰 잔액도 모두 일치합니다.');
                setSyncStatus(SYNC_STATUS.COMPLETE)
                return true;
            }
    
            // 일치하지 않으면 RPC 에서 가져온 tx id 리스트 중에 서버에서 가져온 최신 tx id가 포함되어 있는지 확인한다
            let newTxIds: any[] = [];
    
            if (!serverTxIds || serverTxIds.length === 0) {
                if (!rpcTxList || rpcTxList.length === 0) {
                console.log('RPC의 tx id 정보가 없음.');
                setSyncStatus(SYNC_STATUS.COMPLETE)
                return true;
                }
                newTxIds = rpcTxList;
                console.log('serverTxIds가 없으므로 rpcTxIds를 그대로 사용.');
            } else {
                const serverLatestTxId = serverTxIds[0].transaction_hash;
                console.log(`serverLatestTxId : `, serverLatestTxId)
                const isIncluded = rpcTxList.some((rpcTx: any) => rpcTx.signature === serverLatestTxId);
                
                if (isIncluded) {
                    console.log('RPC의 tx id 리스트에 서버의 최신 tx id가 포함되어 있습니다.');
                        
                    // RPC tx id 최신 ~ 서버 tx id 최신 사이의 tx id를 추출한다
                    const startIndex = rpcTxList.findIndex((rpcTx: any) => rpcTx.signature.toString() === serverLatestTxId.toString());
                    if (startIndex !== -1 && startIndex < rpcTxList.length - 1) {
                        // 서버 txid 위치 다음 ~ rpc 최신 txid 사이
                        newTxIds = rpcTxList.slice(startIndex + 1);
                        console.log('추출된 새로운 tx id 리스트 1 :', newTxIds);
                    }
                } else {
                    console.log('RPC의 tx id 리스트에 서버의 최신 tx id가 포함되어 있지 않습니다.');
                    
                    const isIncludedInAdditional = rpcTxList.some((tx: any) => tx.signature === serverLatestTxId);
            
                    if (isIncludedInAdditional) {
                        console.log('RPC에서 가져온 tx id 리스트에 서버의 최신 tx id가 포함되어 있습니다.');
                        const startIndex = rpcTxList.findIndex((tx: any) => tx.signature === serverLatestTxId);
                        
                        if (startIndex !== -1) {
                        // 서버 txid 위치 ~ rpc 최신 txid 사이
                        newTxIds = rpcTxList.slice(startIndex + 1);
                        console.log('추출된 새로운 tx id 리스트 2 :', newTxIds);
                        } else {
                        console.warn('RPC tx id 리스트에서 서버의 최신 tx id를 찾을 수 없습니다.');
                        setSyncStatus(SYNC_STATUS.ERROR)
                        return false;
                        }
                    } else {
                        console.log('RPC에서 가져온 tx id 리스트에 서버의 최신 tx id가 포함되어 있지 않습니다.');
                        setSyncStatus(SYNC_STATUS.ERROR)
                        return false;
                    }
                }
            }
            console.log('8 syncAllTransactionHistory 트랜잭션 상세 요청할 newTxIds:', newTxIds)
    
            if(0 === newTxIds.length) {
                console.log('9 syncAllTransactionHistory DB 현행화 할 tx 없음. 종료')
                setSyncStatus(SYNC_STATUS.COMPLETE)
                return true;
            } 

            setCountSyncComplete(newTxIds.length);
            
            // 추출한 tx 리스트의 트랜잭션 상세 정보를 순차적으로 가져와 서버에 추가한다
            let countProcess = 0;
            let dbTxSyncStatus = SYNC_STATUS.PROCESSING.toLowerCase();
            for (const txInfo of newTxIds) {
                if (txInfo) {
                    setSyncStatus(SYNC_STATUS.PROCESSING)
            
                    if (reqTxStopRef.current) {
                        console.log(`############### syncAllTransaction reqTxStop 1-1`, reqTxStopRef.current)
                        setSyncStatus(SYNC_STATUS.STOP)
                        return true;
                    }
        
                    // txInfo 정보 없음
                    if (!txInfo) {
                        console.warn(`10. syncAllTransactionHistory tx or tx.transaction.message is null`)
                        continue;
                    }
        
                    let parsedTransaction;
                    
                    // 트랜잭션의 데이터 파싱해서 서버에 저정할 데이터 생성
                    // 네이티브 트랜잭션(normal, internal)
                    // 토큰 트랜잭션
                    
                    // txInfo에 tokenName 또는 tokenSymbol 등이 있다면 토큰 트랜잭션으로 간주
                    let symbol = tonNetwork?.currency.toLowerCase() || 'ton';
                    if (txInfo.tokenName || txInfo.tokenSymbol) {
                        parsedTransaction = await parseTokenTransaction(txInfo, walletAddress);
                        console.log(`11-1. syncLimitTransaction Token 트랜잭션 파싱 결과: `, parsedTransaction);
                        symbol = parsedTransaction?.tokenSymbol?.toLowerCase() || 'beptoken';
                    } else {
                        parsedTransaction = await parseTonCoinTransaction(txInfo, walletAddress);
                        console.log(`11-2. syncLimitTransaction 네이티브 트랜잭션 파싱 결과: `, parsedTransaction);
                    }
            
                    if (!parsedTransaction) {
                        console.log(`12. syncLimitTransaction parsedTransaction 데이터 없음 `);
                        continue;
                    }
                    
                    // newTxIds의 마지막 인덱스 도달 여부 확인
                    const lastNewTxIdsCount = newTxIds.length-1;
                    const isLastIndex = countProcess === (newTxIds.length-1);

                    console.log(`13. syncAllTransactionHistory lastNewTxIdsCount : ${lastNewTxIdsCount} |  countProcess : ${countProcess} | isLastIndex : ${isLastIndex}`)

                    dbTxSyncStatus = SYNC_STATUS.PROCESSING.toLowerCase();
                    if (isLastIndex) {
                        dbTxSyncStatus = SYNC_STATUS.COMPLETE.toLowerCase();
                        console.log(`14. syncAllTransactionHistory 마지막 값 도달 DB TX 현행화 상태 설정 : `, dbTxSyncStatus)
                    }
        
                    console.log(`15. syncLimitTransaction symbol : `, symbol);
        
                    const response = await addServerTxHistory(
                        walletAddress,
                        selNetwork,
                        telegramId,
                        parsedTransaction.hash,
                        parsedTransaction.blockNumber.toString(),
                        parsedTransaction.timestamp.toString(),
                        symbol,
                        parsedTransaction.mintAddress,
                        parsedTransaction.from.hash,
                        parsedTransaction.to.hash,
                        parsedTransaction.amount.toString(),
                        parsedTransaction.fee.value.toString(),
                        parsedTransaction.rentFee.value.toString(),
                        parsedTransaction.transfer_type,
                        syncKeyRef.current,
                        dbTxSyncStatus
                    );
                    
                    if(true === response.result && response.data.length > 0) {
                        // DB 현행화 진행 수량
                        countProcess = countProcess+1;
                        setCountSyncProcess(countProcess)
        
                        await updateLastTxTo(parsedTransaction, walletAddress, symbol);
                    } else if(false === response.result && 
                        (323 === response.error_code || 324 === response.error_code || 325 === response.error_code)) {
                        console.warn(`16-1 syncAllTransactionHistory addServerTxHistory fail `, response.message)
                        setSyncStatus(SYNC_STATUS.SYNCKEY_ERROR)
                        return true;
                    } else if(false === response.result && 301 === response.error_code) {
                        console.warn(`16-2 syncAllTransactionHistory addServerTxHistory overlap `, response.message)
                        setSyncStatus(SYNC_STATUS.STOP)
                        return true;
                    } else if(false === response.result) {
                        console.warn(`16-3 syncAllTransactionHistory addServerTxHistory fail `, response.message)
                        setSyncStatus(SYNC_STATUS.STOP)
                        return true;
                    }
                }
            }

            console.log('12 syncAllTransactionHistory DB 현행화 완료:')
            setSyncStatus(SYNC_STATUS.COMPLETE);
            return true;

        } catch (error) {
            console.error(`Error syncAllTransactionHistory : `, error)
            setSyncStatus(SYNC_STATUS.ERROR);
            setReqTxStop(false);
            return false;
        } finally {
            // 동작중 flag 초기화
            setIsRunAllTransactionHistory(false);
            setReqTxStop(false);
        }
    }
    
    // ─────────────────────────────────────────────────────────────
    // 9) TonCoin 트랜잭션 파싱
    //    여기서는 단순히 hash, from, to, value, blockNumber, timestamp parse
    // ─────────────────────────────────────────────────────────────
    /**
     * tx 정보를 파싱해서 서버에 저장할 데이터
     * @param tx : 트랜잭션 상세 정보
     * @param walletAddress 
     * @returns 
     */
    const parseTonCoinTransaction = async (
        tx: any, 
        walletAddress: string
    ) => {
        try {
            console.log(`1. parseBnbTransaction walletAddress: ${walletAddress}`, tx);
        
            // 1) 토큰 전송인 경우 → 파싱 제외
            
            // 2) blockNumber, timeStamp (숫자형 변환)
            const slot = Number(tx.slot);
            const blockTime = Number(tx.timeStamp);
        
            // 3) from / to
            const fromAddr = (tx.from || 'none');
            const toAddr = (tx.to || 'none');
    
            // 4) value (TON 전송량, nanoton 단위)
            const valueTon: string = tx.amount;
        
            // 5) 수수료 계산
            const feeNanoton = tx.gasUsed;
        
            // 6) 출금/입금 구분
            let transfer_type = '';
            if (tx.type === 'withdrawal') {
                transfer_type = 'withdrawal';
            } else if (tx.type === 'receive') {
                transfer_type = 'receive';
            }
            
            // 7) parseData 생성
            const parseData = {
                // 트랜잭션 식별
                hash: tx.hash,          // 실제 트랜잭션 해시
                transaction_hash: tx.hash,
                blockNumber: slot,
                blockTime,
                timestamp: blockTime,
            
                // 주소/금액
                from: { hash: fromAddr },
                to: { hash: toAddr },
                amount: tx.value,
                value: valueTon,
                transfer_type,
            
                // 가스 수수료
                fee: { value: feeNanoton.toString() },
                rentFee: { value: '0' }, // rentFee는 0 처리
                mintAddress: tx.mintAddress,         // 네이티브 BNB이므로 빈 문자열
            
                // 기타 정보
                input: tx.input,
                methodId: tx.methodId,
                functionName: tx.functionName
            };
        
            console.log('parseBnbTransaction result:', parseData);
            return parseData;
        } catch (error) {
            console.error('parseBnbTransaction error:', error);
            return null;
        }
    }

    /**
     * 토큰 트랜잭션 파싱
     * @param tx 
     * @param walletAddress 
     */
    const parseTokenTransaction = async (
        tx: any, 
        walletAddress: string
    ) => {
        try {
        console.log(`1. parseTokenTransaction walletAddress : ${walletAddress} | tx : `, tx);
        
        // 1) 토큰 전송인 경우 → 파싱 제외
            
            // 2) blockNumber, timeStamp (숫자형 변환)
            const slot = Number(tx.slot);
            const blockTime = Number(tx.timeStamp);
        
            // 3) from / to
            const fromAddr = (tx.from || 'none');
            const toAddr = (tx.to || 'none');
    
            // 4) value (TON 전송량, nanoton 단위)
            const valueTon: string = tx.amount;
        
            // 5) 수수료 계산: gasUsed * gasPrice (wei) → BNB
            const feeNanoton = tx.gasUsed;
        
            // 6) 출금/입금 구분
            let transfer_type = '';
            if (tx.type === 'withdrawal') {
                transfer_type = 'withdrawal';
            } else if (tx.type === 'receive') {
                transfer_type = 'receive';
            }
            
            // 6. 토큰 거래이므로 mintAddress는 tx에 저장된 토큰 컨트랙트 주소
            const networkSymbol = tonNetwork?.symbol.toLowerCase() || 'ton'
            const mintAddress = tx.mintAddress;
            const tokenInfo = await getNetworkTokenDataByAddress(networkSymbol, mintAddress);
            console.log(`2. parseTokenTransaction tokenInfo : `, tokenInfo);

            let tokenSymbol;
            if (tokenInfo && tokenInfo.symbol) {
                tokenSymbol = tokenInfo.symbol.toString();
            } else {
                tokenSymbol = tx.tokenSymbo.toString();
            }

            
            
            // 7) parseData 생성
            const parseData = {
                // 트랜잭션 식별
                hash: tx.hash,          // 실제 트랜잭션 해시
                transaction_hash: tx.hash,
                blockNumber: slot,
                blockTime,
                timestamp: blockTime,
            
                // 주소/금액
                from: { hash: fromAddr },
                to: { hash: toAddr },
                amount: tx.value,        // wei 단위
                value: valueTon,         // BNB 단위
                transfer_type,           // 'withdrawal' | 'receive' | ''
                
                // 추가 정보 (필요 시)
                tokenName: tokenSymbol,
                tokenSymbol: tokenSymbol,
                tokenDecimal: tx.tokenDecimal,
                
                // 필요에 따라 다른 필드들도 추가 가능
                transactionIndex: tx.transactionIndex,
                gas: tx.gas,
                gasPrice: tx.gasPrice,
                gasUsed: tx.gasUsed,
                cumulativeGasUsed: tx.cumulativeGasUsed,
                confirmations: tx.confirmations,

                // 가스 수수료 (wei로 저장)
                fee: { value: feeNanoton.toString() },
                rentFee: { value: '0' }, // rentFee는 0 처리
                mintAddress: tx.mintAddress,
                
                // 기타 정보
                input: tx.input,
                methodId: tx.methodId,
                functionName: tx.functionName
            };

            console.log(`2. parseTokenTransaction - parseData : `, parseData);
            return parseData;
        } catch (error) {
            console.error("parseTokenTransaction error:", error);
            return null;
        }
    }

    /**
       * server에 tx 데이터 추가 요청
       * 
       * @param walletAddress 
       * @param network 
       * @param tid 
       * @param txId 
       * @param blockNum 
       * @param timeStamp 
       * @param coinName 
       * @param from 
       * @param to 
       * @param amount 
       * @param fee 
       * @param rentFee 
       * @param transferType 
       * @param syncKey 
       * 
       * @returns 
       */
    const addServerTxHistory = async (
        walletAddress: string, 
        network: string, 
        tid: string, 
        txId:string, 
        blockNum:string, 
        timeStamp:string, 
        coinName:string, 
        mintAddress:string, 
        from:string, 
        to:string, 
        amount:string, 
        fee:string,
        rentFee:string,	
        transferType:string,
        syncKey:string,
        syncStatus:string
    ): Promise<any | null> => {
        try {
            // WASM 함수 호출
            const response = await add_server_tx_history(
                process.env.REACT_APP_QWALLET_SERVER_URL!,
                walletAddress,
                network,
                tid,
                txId,
                blockNum.toString(),
                timeStamp.toString(),
                coinName,
                mintAddress,
                from,
                to,
                amount.toString(),
                fee.toString(),
                rentFee.toString(),
                transferType,
                syncKey,
                syncStatus
            );
            console.log(`response`, response)

            const result = JSON.parse(response)
            console.log(`JSON.parse result`, result)

            return result
        } catch (error) {
            console.error('qWallet 서버에서 tx 데이터 추가 중 오류 발생:', error);
            // throw error;
            return null
        }
    }

    /**
       * tx id 리스트 요청
       * 
       * @param walletAddress 
       * @param network 
       * @param tid 
       * @param limit 
       * @returns 
       */
    const getServerTxIdList = async (
        walletAddress: string, 
        network: string, 
        tid: string, 
        page: number=1, 
        take: number=5
    ): Promise<any | null> => {
        try {
            if (!walletAddress || !network || !tid) {
                console.error('유효하지 않은 파라미터: walletAddress, network, tid는 필수입니다.');
                return null;
            }

            const result = await get_server_tx_id_list(
                process.env.REACT_APP_QWALLET_SERVER_URL!,
                walletAddress,
                network,
                tid,
                page,
                take,
            );
            console.log(`getServerTxIdList result`, result)

            const response = JSON.parse(result)
            console.log(`JSON.parse response`, response)

            // key name 변경
            if(true === response.result && response.data.length > 0) {
                const transformedData = response.data.map((item: any) => ({
                    slot: item.th_blocknum,
                    transaction_hash: item.th_txid
                }));

                return transformedData
            } else {
                return null;
            }
        } catch (error) {
            console.error('qWallet 서버에서 tx id 리스트 요청 중 오류 발생:', error);
            throw error;
        }
    }

    // ─────────────────────────────────────────────────────────────
    // 10) 마지막 Tx를 전송내역 형태로 추가
    // ─────────────────────────────────────────────────────────────
    const updateLastTxTo = async (tx: any, accountAddress: string, symbol: string) => {
        const txId = tx.transaction_hash;
        const fromAddress = tx.from.hash;
        const detailDate = moment(tx.timestamp * 1000).format('YYYY. M. D');

        let amount = '0';
        if (tx.mintAddress) {
            amount = '0'
        } else {
            // TON
            amount = '0'
        }
    
        symbol = symbol.substring(0, 4);
    
        const txData: SyncTx = {
            id: `${txId}`,
            type:
            fromAddress.toLowerCase() === accountAddress.toLowerCase()
                ? i18next.t('template_wallet_screen_message_09') // 출금
                : i18next.t('template_wallet_screen_message_10'), // 입금
            amount: amount,
            currency: symbol.toLocaleUpperCase(),
            date: detailDate,
            direction: fromAddress.toLowerCase() === accountAddress.toLowerCase() ? 'out' : 'in'
        };
    
        setLastSyncTxs((prev) => {
            const updated = [txData, ...prev];
            if (updated.length > 3) {
                updated.pop();
            }
            return updated;
        });
        return txData;
    };

    // ─────────────────────────────────────────────────────────────
    // 11) 토큰 정보 조회
    // ─────────────────────────────────────────────────────────────
    const getTokenInfo = async (mintAddress: string) => {
        try {
            
            // name(), symbol() API가 ABI에 있어야 호출 가능
            // 여기서는 임시로
            const name = ''
            const symbol = ''
            const decimals = 0
            return {
                name,
                symbol,
                decimals
            };
        } catch (error) {
            console.error('getTokenInfo error:', error);
            return null;
        }
    };
    
    /**
       * qWallet 서버에서 마지막 3개 tx data 요청
       * @param walletAddress : 지갑 주소
       * @param network : 네트워크 종류
       * @param tid : 텔레그럄 id
       * @param tokenName : 토큰 이름
       * @param page : 페이지 번호
       * @param take : 페이지당 표시할 데이터 수
       * 
       * @returns jsonArray
       */
    const getServerTxHistory = async (
        walletAddress: string, 
        network: string, 
        tid: string, 
        tokenName: string, 
        page: number=1, 
        take: number=5
    ): Promise<any | null> => {
        try {
            const result = await get_server_tx_history(
                process.env.REACT_APP_QWALLET_SERVER_URL!,
                walletAddress,
                network,
                tid,
                tokenName,
                page,
                take
            );
            
            console.log(`getServerTxHistory result`, result)

            const response = JSON.parse(result)
            console.log(`JSON.parse response`, response)

            // key name 변경
            if(true === response.result && response.data.length > 0) {
                
                console.log(`response.data`, response.data)

                const transformedData = response.data.flatMap((item: any) => {
                    const baseTransaction = {
                    slot: Number(item.th_blocknum),
                    blockTime: item.th_timestamp,
                    from: {hash: item.th_from},
                    to: {hash: item.th_to},
                    amount: item.th_amount,
                    value: (item.th_amount / Math.pow(10, item.tokenInfo?.ti_decimal)).toString(),
                    fee: {
                        value: (item.th_fee / Math.pow(10, 9)).toString(),
                    },
                    rentFee: {
                        value: item.th_rentfee
                    },
                    timestamp: item.th_timestamp,
                    transaction_hash: item.th_txid,
                    coin_type: item.tokenInfo?.ti_type,
                    coin_name: item.th_coinname
                    };
                
                    // 동일한 지갑 주소인 경우 (자체 전송)
                    if (item.th_from === item.th_to) {
                    return [
                        {
                            ...baseTransaction,
                            transfer_type: 'receive'
                        },
                        {
                            ...baseTransaction,
                            transfer_type: 'withdrawal'
                        }
                    ];
                    }
                
                    // 일반적인 전송인 경우
                    return [{
                        ...baseTransaction,
                        transfer_type: item.th_transfertype
                    }];
                });

                return {items: transformedData}
            } else {
                return {items: []}
            }
        } catch (error) {
            console.error('qWallet 서버에서 tx 데이터 요청 중 오류 발생:', error);
            return {items: []}
        }
    }

    /**
     * 블록 익스플로러 url
     * @param chainName 
     * @param address 
     */
    const getBlockScanUrl = 
    async (
        txid: string
    ): Promise<string> => {
        const blockExplorerUrl = tonNetwork?.blockexp || '';
        return `${blockExplorerUrl}/tx/${txid}`;
    }
    
    /**
	 * address 유효성 검사
	 * @param address 
	 * @returns 
	 */
	const isValidAddress = async (address: string): Promise<boolean> => {
		try {
			if (address) {
				try {
					Address.parse(address); // 주소 파싱 시 오류가 발생하면 유효하지 않음
					return true; // 파싱 성공 -> 유효한 주소
				} catch (error) {
					return false; // 파싱 실패 -> 유효하지 않은 주소
				}
            }
			return false; // address가 제공되지 않거나 다른 네트워크일 경우 기본적으로 false 반환
		} catch (error) {
			console.error(`TonContext isValidAddress error : ${error}`);
			return false; // 예외가 발생하면 false 반환
		}
	};
    
    // ─────────────────────────────────────────────────────────────
    // Provider 리턴
    // ─────────────────────────────────────────────────────────────
    return (
        <TonContext.Provider
            value={{
            networkName,
            balance,
            countSyncProcess,
            countSyncComplete,
            syncStatus,
            lastSyncTxs,
            reqTxStop,
            startDbSyncTimer,
            stopDbSyncTimer,
            getBalance,
            getBalanceFromServer,
            getTokenBalance,
            getTokenBalanceFromServer,
            estimateGas,
            sendTransactionWithPrivateKey,
            getTotalTxCount,
            stopRequestTxData,
            syncAllTransaction,
            getTokenInfo,
            getServerTxHistory,
            getBlockScanUrl,
            isValidAddress
            }}
        >
            {children}
        </TonContext.Provider>
    );

};
